/**
 * API contracts.
 *
 * Every HTTP route in apps/api validates its body against a schema from this
 * file, and apps/mobile + apps/admin infer their client types from the same
 * schemas. If a route and a screen disagree, one of them is not using this
 * file and that is the bug.
 */
import { z } from 'zod';
import {
  STRUCTURES,
  GENDERS,
  ORIENTATIONS,
  CIRCLE_KINDS,
  CONSENT_KINDS,
  REPORT_REASONS,
  MODERATION_VERDICTS,
  PLANS,
  MEDIA_GATE_STATES,
  MESSAGE_KINDS,
  SUPPORTED_LOCALES,
  VERIFICATIONS,
  REVEAL_METHODS,
} from './domain.js';
import { MIN_PROMPT_ANSWERS, MAX_PROMPT_ANSWERS, PROMPT_ANSWER_MIN_CHARS, PROMPT_ANSWER_MAX_CHARS } from './prompts.js';

/* ------------------------------- primitives ------------------------------ */

export const zUuid = z.string().uuid();
/** Indian mobile numbers only, E.164. Phone is the only identity we accept (§06). */
export const zPhone = z.string().regex(/^\+91[6-9]\d{9}$/, 'Enter a valid Indian mobile number');
export const zLocale = z.enum(SUPPORTED_LOCALES);

/* ---------------------------------- auth --------------------------------- */

export const zOtpRequest = z.object({
  phone: zPhone,
  /**
   * Cloudflare Turnstile. Optional in the contract because the server only
   * demands one once TURNSTILE_SECRET_KEY is set (see api/src/lib/turnstile.ts),
   * so a build shipped before the widget exists keeps working until then.
   */
  turnstileToken: z.string().min(1).max(2048).optional(),
});

export const zOtpRequestResponse = z.object({
  requestId: zUuid,
  expiresInSec: z.number(),
  /**
   * Seconds until another code may be asked for, straight off the server's
   * backoff ladder (otpResendWaitSec). The client displays this rather than
   * recomputing it, so the resend button can never disagree with the API that
   * enforces it.
   */
  nextResendInSec: z.number(),
  /** Present only when SMS_PROVIDER=mock, so local dev works without an SMS bill. */
  devOtp: z.string().optional(),
});

export const zOtpVerify = z.object({
  requestId: zUuid,
  code: z.string().length(6),
  deviceId: z.string().min(8).max(128),
});

export const zSession = z.object({
  token: z.string(),
  userId: zUuid,
  onboardingStep: z.enum([
    'age', 'identity', 'liveness', 'structure', 'prompts', 'privacy', 'waitlist', 'done',
  ]),
});

/* ------------------------------- onboarding ------------------------------ */

export const zAgeGate = z.object({
  dob: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});

export const zIdentity = z.object({
  displayName: z.string().min(1).max(40),
  gender: z.enum(GENDERS),
  genderSelfDescribe: z.string().max(40).optional(),
  pronouns: z.string().max(30),
  orientations: z.array(z.enum(ORIENTATIONS)).min(1).max(4),
  locale: zLocale.default('en'),
  /**
   * The coarse zone. Still accepted directly so older app builds keep working;
   * new builds send areaId and let the server derive this.
   */
  clusterId: z.string().min(2).max(40).optional(),
  /**
   * The fine-grained area the member actually picked (§14). The server
   * resolves it to its zone and stores both - other members only ever see the
   * zone, because a neighbourhood identifies someone and a zone does not.
   */
  areaId: z.string().min(2).max(80).optional(),
}).refine((v) => Boolean(v.clusterId || v.areaId), {
  message: 'Pick where you live.',
  path: ['areaId'],
});

/**
 * The awareness attestation (§05). `partnerAware: false` does not create an
 * account — the API returns a 403 with a resources payload, by design.
 */
export const zStructureDeclaration = z.object({
  structure: z.enum(STRUCTURES),
  partnerAware: z.boolean().optional(),
});

export const zPromptAnswer = z.object({
  promptId: z.string().min(2).max(8),
  body: z.string().min(PROMPT_ANSWER_MIN_CHARS).max(PROMPT_ANSWER_MAX_CHARS),
});

export const zPromptAnswers = z.object({
  answers: z.array(zPromptAnswer).min(MIN_PROMPT_ANSWERS).max(MAX_PROMPT_ANSWERS),
});

/**
 * Contact hashes are computed on the device (HMAC over E.164 with a server
 * pepper). Raw numbers must never reach this endpoint (§07, §11).
 */
export const zPrivacySetup = z.object({
  contactHashes: z.array(z.string().regex(/^[0-9a-f]{64}$/)).max(5000).default([]),
  circleBlocks: z.array(z.object({
    kind: z.enum(CIRCLE_KINDS),
    valueId: z.number().int().positive(),
  })).max(50).default([]),
  hideFromContacts: z.boolean().default(true),
  blurUntilMutual: z.boolean().default(true),
  appLockEnabled: z.boolean().default(false),
  maskNotifications: z.boolean().default(true),
});

export const zLivenessSubmit = z.object({
  /** data: URI or base64 JPEG. Never persisted — checked, scored, discarded. */
  image: z.string().min(100),
});

export const zLivenessResult = z.object({
  passed: z.boolean(),
  reason: z.string().optional(),
  mock: z.boolean(),
});

/* -------------------------------- profile -------------------------------- */

export const zProfileUpdate = z.object({
  displayName: z.string().min(1).max(40).optional(),
  bio: z.string().max(300).optional(),
  locale: zLocale.optional(),
  clusterId: z.string().min(2).max(40).optional(),
  availabilityNote: z.string().max(120).optional(),
  travelMode: z.boolean().optional(),
});

export const zPublicProfile = z.object({
  id: zUuid,
  displayName: z.string(),
  ageYears: z.number().int(),
  structure: z.enum(STRUCTURES),
  pronouns: z.string(),
  orientations: z.array(z.enum(ORIENTATIONS)),
  verifications: z.array(z.enum(VERIFICATIONS)),
  clusterId: z.string(),
  distanceKm: z.number().nullable(),
  locales: z.array(zLocale),
  /** Blurhash placeholders only until photos are unlocked (§04, §07). */
  photos: z.array(z.object({ id: zUuid, blurhash: z.string(), url: z.string().nullable() })),
  photosUnlocked: z.boolean(),
  promptAnswers: z.array(z.object({
    id: zUuid, promptId: z.string(), body: z.string(),
  })),
  cosigned: z.boolean(),
  availabilityNote: z.string().nullable(),
  sharedContactCount: z.number().int(),
  smallWorldWarning: z.boolean(),
});

/* ----------------------------- partner links ----------------------------- */

export const zPartnerInvite = z.object({ phone: zPhone });
export const zPartnerAction = z.object({ linkId: zUuid });

/* ------------------------------- consent --------------------------------- */

export const zConsentGrant = z.object({
  kind: z.enum(CONSENT_KINDS),
  scopeId: zUuid.optional(),
  source: z.enum(['onboarding', 'settings', 'in_thread']).default('settings'),
});
export const zConsentRevoke = z.object({ consentId: zUuid });

/* ------------------------------- discovery ------------------------------- */

export const zDiscoveryFilters = z.object({
  minAge: z.number().int().min(18).max(99).default(18),
  maxAge: z.number().int().min(18).max(99).default(99),
  maxDistanceKm: z.number().int().min(1).max(500).default(25),
  structures: z.array(z.enum(STRUCTURES)).default([]),
  hideCouples: z.boolean().default(false),
  requireCosigned: z.boolean().default(false),
  requireVerified: z.boolean().default(true),
  locales: z.array(zLocale).default([]),
});

export const zFeedQuery = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(20).default(10),
});

export const zPass = z.object({ profileId: zUuid });

/**
 * There is no "like" (§04). Interest costs a written reply to a specific
 * prompt answer, which is the ratio intervention that doesn't select for wealth.
 */
export const zReply = z.object({
  profileId: zUuid,
  promptAnswerId: zUuid,
  body: z.string().min(PROMPT_ANSWER_MIN_CHARS).max(600),
});

export const zReplyResult = z.object({
  threadId: zUuid.nullable(),
  status: z.enum(['sent', 'matched', 'quota_exhausted', 'blocked', 'held_for_review']),
  repliesLeftToday: z.number().int(),
  message: z.string().optional(),
});

/* -------------------------------- threads -------------------------------- */

export const zSendMessage = z.object({
  kind: z.enum(MESSAGE_KINDS).default('text'),
  body: z.string().max(2000).optional(),
  mediaId: zUuid.optional(),
});

export const zMediaGateResponse = z.object({ state: z.enum(MEDIA_GATE_STATES) });

export const zDatePlan = z.object({
  place: z.string().min(2).max(120),
  startsAt: z.string().datetime(),
  /** Contact who receives the plan and the escalation if check-in is missed (§08). */
  checkInContactName: z.string().min(1).max(60).optional(),
  checkInContactPhone: zPhone.optional(),
});

export const zCheckIn = z.object({ datePlanId: zUuid, ok: z.boolean() });

/* ---------------------------- topic gates (§05) ---------------------------
 * One contiguous block, added whole so it merges cleanly. The two constants it
 * needs come in on their own import line rather than being appended to the
 * import list at the top of the file, for the same reason — imports are hoisted,
 * so this is legal and it keeps the edit to a single hunk.
 * -------------------------------------------------------------------------- */

import { GATE_TOPICS, TOPIC_GATE_STATES } from './domain.js';

/** Which gate. A short named list, never free text — see domain.ts. */
export const zGateTopic = z.enum(GATE_TOPICS);

/**
 * The four moves. 'close' carries no reason field and never will: a gate you
 * have to justify closing is not a gate, so there is nowhere for the client to
 * put an explanation and nowhere for the server to store one.
 */
export const zGateAction = z.object({
  topic: zGateTopic,
  action: z.enum(['request', 'accept', 'decline', 'close']),
});

/**
 * One gate as one person sees it. `theirs` is what the viewer is PERMITTED to
 * know about the other side: a decline arrives here as 'ordinary', identical to
 * a request nobody answered (§07). `open` is true only when both sides are
 * open. Neither who moved it last nor when is in this shape, deliberately.
 */
export const zGateView = z.object({
  topic: zGateTopic,
  label: z.string(),
  mine: z.enum(TOPIC_GATE_STATES),
  theirs: z.enum(TOPIC_GATE_STATES),
  open: z.boolean(),
  canRequest: z.boolean(),
});

export const zGatesResponse = z.object({ gates: z.array(zGateView) });
export const zGateResponse = z.object({ gate: zGateView });

export type GateAction = z.infer<typeof zGateAction>;
export type GateView = z.infer<typeof zGateView>;

/* -------------------------- end of topic gates ---------------------------- */

/* --------------------------------- safety -------------------------------- */

export const zReport = z.object({
  subjectUserId: zUuid,
  reason: z.enum(REPORT_REASONS),
  detail: z.string().max(1000).optional(),
  threadId: zUuid.optional(),
  messageId: zUuid.optional(),
});

export const zBlock = z.object({ subjectUserId: zUuid });

export const zPanic = z.object({
  lat: z.number().optional(),
  lon: z.number().optional(),
  datePlanId: zUuid.optional(),
});

/* -------------------------------- billing -------------------------------- */

export const zCheckout = z.object({ plan: z.enum(PLANS) });
export const zCheckoutResult = z.object({
  orderId: z.string(),
  amountPaise: z.number().int(),
  /** UPI intent string for the one-time pass path (§12) — no mandate required. */
  upiIntent: z.string().optional(),
  provider: z.string(),
  mock: z.boolean(),
});

/* ------------------------------ admin / mod ------------------------------ */

export const zModerationVerdict = z.object({
  caseId: zUuid,
  verdict: z.enum(MODERATION_VERDICTS),
  note: z.string().max(1000).optional(),
});

export const zQueueQuery = z.object({
  status: z.enum(['open', 'closed', 'all']).default('open'),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

/** Ratio guardrail dashboard (§13). */
export const zRatioSnapshot = z.object({
  clusterId: z.string(),
  men: z.number().int(),
  womenNb: z.number().int(),
  ratio: z.number(),
  paused: z.boolean(),
});

/* ------------------------------ inferred types --------------------------- */

export type OtpRequest = z.infer<typeof zOtpRequest>;
export type OtpVerify = z.infer<typeof zOtpVerify>;
export type Session = z.infer<typeof zSession>;
export type Identity = z.infer<typeof zIdentity>;
export type StructureDeclaration = z.infer<typeof zStructureDeclaration>;
export type PromptAnswerInput = z.infer<typeof zPromptAnswer>;
export type PrivacySetup = z.infer<typeof zPrivacySetup>;
export type PublicProfile = z.infer<typeof zPublicProfile>;
export type DiscoveryFilters = z.infer<typeof zDiscoveryFilters>;
export type ReplyInput = z.infer<typeof zReply>;
export type ReplyResult = z.infer<typeof zReplyResult>;
export type SendMessageInput = z.infer<typeof zSendMessage>;
export type DatePlanInput = z.infer<typeof zDatePlan>;
export type ReportInput = z.infer<typeof zReport>;
export type CheckoutResult = z.infer<typeof zCheckoutResult>;
export type RatioSnapshot = z.infer<typeof zRatioSnapshot>;
export type LivenessResult = z.infer<typeof zLivenessResult>;

/* -------------------------------- likes ---------------------------------- */

export const zLike = z.object({ profileId: zUuid });

export const zLikeResult = z.object({
  status: z.enum(['sent', 'already_liked', 'quota_exhausted', 'blocked']),
  likesLeftToday: z.number().int(),
  message: z.string().optional(),
});

/**
 * One entry in "who likes you". Masked until revealed: the client is given
 * enough to know a person is there and nothing that identifies them.
 */
export const zLikeInboxEntry = z.object({
  id: zUuid,
  createdAt: z.string(),
  revealed: z.boolean(),
  /** Present only once revealed. */
  profile: z.unknown().nullable(),
  /** Always present — a shape, never a face (§04). */
  hint: z.object({
    clusterName: z.string(),
    structure: z.string(),
    verified: z.boolean(),
  }),
});

export const zRevealRequest = z.object({
  likeId: zUuid,
  /**
   * Which rung the client believes it is on. The server re-derives this and
   * refuses a mismatch — a client that asks for 'free' when free is spent gets
   * told the real price rather than a free reveal.
   */
  method: z.enum(REVEAL_METHODS),
  /**
   * Proof the advertisement was actually watched. Required for method 'ad'.
   * Verified server-side against the ad network's callback — see
   * services/likes.ts.
   */
  adToken: z.string().min(8).max(512).optional(),
});

export const zRevealState = z.object({
  freeLeft: z.number().int(),
  adLeft: z.number().int(),
  hasSubscription: z.boolean(),
  /** What the NEXT reveal will cost. */
  nextMethod: z.enum(REVEAL_METHODS).nullable(),
  resetsAt: z.string(),
});

/* --------------------------- admin plan pricing -------------------------- */

export const zPlanPriceUpdate = z.object({
  plan: z.enum(PLANS),
  amountPaise: z.number().int().min(0).max(10_000_00),
});

/* ---------------------------- account deletion --------------------------- */

/** Why someone left. Optional, never required, and never shown to another member. */
export const DELETION_REASONS = [
  'found_someone',
  'not_useful',
  'privacy_concern',
  'harassment',
  'taking_break',
  'other',
] as const;
export type DeletionReason = (typeof DELETION_REASONS)[number];

export const zAccountDeletion = z.object({
  reason: z.enum(DELETION_REASONS).optional(),
  note: z.string().max(500).optional(),
  /**
   * Explicit intent. The typed confirmation belongs on the client, where it can
   * be written in the member's own language — the server only needs to know a
   * deliberate action happened rather than a stray DELETE.
   */
  confirm: z.literal(true),
});

/** What someone is about to lose. Shown before the button, never after. */
export const zDeletionPreview = z.object({
  threads: z.number(),
  photos: z.number(),
  partnerLinks: z.number(),
  hasActiveSubscription: z.boolean(),
  subscriptionEndsAt: z.string().nullable(),
  /** Days between the request and the irreversible scrub. */
  graceDays: z.number(),
});
export type DeletionPreview = z.infer<typeof zDeletionPreview>;

/* ======================================================================== *
 * BEGIN line reactions (§04) — one contiguous block, added by the warmth
 * pass. Everything this feature contributes to the contract lives between
 * this marker and the END marker below, including its own import: several
 * people edit this file at once and a second edit up in the import list is
 * a conflict waiting to happen. ES module imports are hoisted, so this is
 * legal wherever it sits.
 * ======================================================================== */

import { LINE_REACTIONS } from './domain.js';

/**
 * A reaction points at a SENTENCE, so the span has to be sentence-sized. The
 * cap is what stops "select the whole answer, tap This line" from quietly
 * recreating the like button §04 argued against: at 200 characters against a
 * 280-character answer you can highlight a long sentence but not a profile.
 */
export const LINE_REACTION_MAX_SPAN_CHARS = 200;

/**
 * And you cannot react to every line either. Three per answer keeps the
 * gesture pointed — reacting to all of it is reacting to the person.
 */
export const MAX_LINE_REACTIONS_PER_ANSWER = 3;

/**
 * Offsets are character positions into the answer body as stored. They are
 * bounded here by the answer length limit so a nonsensical pair is refused
 * before it reaches the database; the service re-checks them against the
 * actual body, which is the check that matters.
 */
export const zLineReactionCreate = z.object({
  promptAnswerId: zUuid,
  startOffset: z.number().int().min(0).max(PROMPT_ANSWER_MAX_CHARS),
  endOffset: z.number().int().min(1).max(PROMPT_ANSWER_MAX_CHARS),
  reaction: z.enum(LINE_REACTIONS),
});

/** DELETE carries no body, so removal is addressed by path (§ routes/reactions.ts). */
export const zLineReactionParams = z.object({
  promptAnswerId: zUuid,
  startOffset: z.coerce.number().int().min(0).max(PROMPT_ANSWER_MAX_CHARS),
});

export const zLineReactionAnswerParams = z.object({ promptAnswerId: zUuid });

/**
 * What comes back. Note what is NOT here: who reacted. The author sees that
 * a line landed and how often; nobody sees a name, and a third party sees
 * only their own marks — a visible tally on someone else's answer is a
 * popularity score, which is the thing this feature exists instead of.
 */
export const zLineReactionSpan = z.object({
  startOffset: z.number().int(),
  endOffset: z.number().int(),
  /** The text those offsets actually select, sliced from the live body. */
  text: z.string(),
  reaction: z.enum(LINE_REACTIONS),
  count: z.number().int().min(1),
  /** Whether the viewer is one of the people in that count. */
  mine: z.boolean(),
});

export const zLineReactionList = z.object({
  promptAnswerId: zUuid,
  /** True when the viewer wrote the answer, so the client can say "someone". */
  authored: z.boolean(),
  spans: z.array(zLineReactionSpan),
});

export const zLineReactionResult = z.object({
  status: z.enum(['created', 'updated', 'unchanged']),
  span: zLineReactionSpan,
  reactionsLeftOnThisAnswer: z.number().int().min(0),
});

export type LineReactionCreate = z.infer<typeof zLineReactionCreate>;
export type LineReactionSpan = z.infer<typeof zLineReactionSpan>;
export type LineReactionList = z.infer<typeof zLineReactionList>;
export type LineReactionResult = z.infer<typeof zLineReactionResult>;

/* ======================================================================== *
 * END line reactions
 * ======================================================================== */

/* ======================= boundary cards (§05, §07) ======================= *
 * Added as one self-contained block, imports included, so it can be read and
 * moved whole. The vocabulary, the 240-character line and the four-per-kind
 * cap all live in domain.ts — they are policy, not validation trivia, and a
 * second copy of a number here is how a client and a server start disagreeing
 * about what "too long" means.
 * ======================================================================== */

import { BOUNDARY_KINDS, BOUNDARY_MAX_CHARS, MAX_BOUNDARIES_PER_KIND } from './domain.js';

/**
 * One line, written once. `position` names the slot within its kind, so PUT is
 * genuinely an upsert: send position 1 twice and the second write replaces the
 * first rather than stacking a duplicate. Omit it and the card takes the next
 * free slot.
 */
export const zBoundaryUpsert = z.object({
  kind: z.enum(BOUNDARY_KINDS),
  body: z.string().trim().min(1, 'Write the line before saving it.').max(BOUNDARY_MAX_CHARS),
  position: z.number().int().min(0).max(MAX_BOUNDARIES_PER_KIND - 1).optional(),
});
export type BoundaryUpsert = z.infer<typeof zBoundaryUpsert>;

export const zBoundaryCard = z.object({
  id: zUuid,
  kind: z.enum(BOUNDARY_KINDS),
  /** BOUNDARY_LABELS[kind], sent along so a client never hardcodes the wording. */
  label: z.string(),
  body: z.string(),
  position: z.number().int(),
  updatedAt: z.string(),
});
export type BoundaryCardView = z.infer<typeof zBoundaryCard>;

/** What someone else's cards look like: the cards, and nothing about the person. */
export const zBoundaryList = z.object({
  cards: z.array(zBoundaryCard),
  limits: z.object({
    maxPerKind: z.number().int(),
    maxChars: z.number().int(),
  }),
});
export type BoundaryList = z.infer<typeof zBoundaryList>;

/**
 * My own cards, plus whether they are actually being shown to anyone. Sharing
 * is a line in the consent ledger (kind `boundary_share`), so this flag is read
 * from there rather than stored beside the cards — see services/boundaries.ts.
 */
export const zBoundaryMine = zBoundaryList.extend({
  sharing: z.object({
    active: z.boolean(),
    /** Plain words for the settings screen. Never "consent recorded". */
    note: z.string(),
  }),
});
export type BoundaryMine = z.infer<typeof zBoundaryMine>;

/** Reordering within one kind. Ids not in the list keep their current order. */
export const zBoundaryReorder = z.object({
  kind: z.enum(BOUNDARY_KINDS),
  ids: z.array(zUuid).min(1).max(MAX_BOUNDARIES_PER_KIND),
});
export type BoundaryReorder = z.infer<typeof zBoundaryReorder>;
